import { ColorHex } from 'react-countdown-circle-timer';
import {
  CalendarTypeLabels,
  CalendarTypes,
  LanguageCode,
  Language,
  TO_DO_STATUS
} from 'enums';
import { t } from 'services/i18n.service';
import COLOR from './color';
import { DropDownSelectedValue, RoutineLevelStatType } from 'interfaces';
import { TodoTasksByStatus } from 'interfaces/initial-states.interface';

export const KEY_BOARD = {
  ENTER: {
    CODE: 13,
    KEY: 'Enter'
  },
  HYPHEN: {
    KEY: '-'
  },
  LETTER_E: {
    KEY: 'e'
  },
  LETTER_S: {
    KEY: 's'
  },
  SPACE: {
    CODE: 32,
    KEY: ' '
  },
  BACKSPACE: {
    KEY: 'Backspace'
  },
  TAB: {
    KEY: 'Tab'
  },
  ARROW_DOWN: {
    KEY: 'ArrowDown'
  },
  ARROW_UP: {
    KEY: 'ArrowUp'
  },
  HOME: {
    KEY: 'Home'
  },
  END: {
    KEY: 'End'
  },
  ESCAPE: {
    KEY: 'Escape'
  }
};

export const MAXIMUM_ALLOWED_MINUTES = 999;
export const MAXIMUM_ALLOWED_SECONDS = 59940;
export const PLATFORMS = {
  WEB: 'web',
  MAC: 'mac',
  WIN: 'win',
  MOB: 'mob',
  IOS: 'ios',
  ANDROID: 'android'
};
export const TAB = {
  TIMING: 'tab_timing',
  MORNING: 'tab_morning',
  MICRO_BREAKS: 'tab_micro_breaks',
  EVENING: 'tab_evening',
  HABIT_PACK: 'tab_habit_packs',
  PACK_INFO: 'tab_pack_info',
  ACTIVITY: 'tab_activity',
  SETTINGS: 'tab_settings',
  APPS_AND_URLS: 'tab_apps_and_urls',
  TEMPLATE_INFO: 'tab_template_info',
  FOCUS_MODE_INFO: 'tab_focus_mode_info',
  PROCRASTINATE: 'procrastinate',
  PROJECTS: 'projects',
  CUSTOM_ROUTINES: 'tab_custom_routines'
};

export const TABS = {
  DEFAULT: [
    TAB.TIMING,
    TAB.MORNING,
    TAB.MICRO_BREAKS,
    TAB.EVENING,
    TAB.CUSTOM_ROUTINES,
    TAB.HABIT_PACK
  ],
  FREESTYLE: [TAB.TIMING, TAB.MICRO_BREAKS],
  STANDALONE: [TAB.ACTIVITY, TAB.PACK_INFO],
  ROUTINE: [TAB.MORNING, TAB.MICRO_BREAKS, TAB.EVENING, TAB.PACK_INFO],
  FOCUS_MODE: [TAB.APPS_AND_URLS, TAB.TEMPLATE_INFO],
  CUSTOM_ROUTINE: [TAB.TIMING, TAB.MORNING, TAB.EVENING, TAB.HABIT_PACK],
  MORNING: [TAB.MORNING],
  BREAKING: [TAB.MICRO_BREAKS],
  EVENING: [TAB.EVENING]
};

export const ACTIVITY_TYPES = {
  MORNING: 'morning_activities',
  BREAKING: 'break_activities',
  EVENING: 'evening_activities',
  STANDALONE: 'standalone_activities',
  LIBRARY: 'library',
  CHOICE: 'choice',
  CUSTOM_ROUTINES: 'custom_routines'
};

/** Tailwind z-index classes for fixed overlays (drawer vs modal stacking). */
export const OVERLAY_Z_INDEX_CLASS = {
  MODAL: 'z-[60]',
  DRAWER: 'z-[99999]',
  /** Modals and body-portaled popovers that must paint above Drawer. */
  MODAL_OVER_DRAWER: 'z-[100000]',
  POPOVER: 'z-[100000]',
  RECURRING_EVENT_SERIES_MODAL: 'z-[100001]'
} as const;

export const MODAL_TYPES = {
  VIDEOS_URLS: 'videos_urls',
  ALLOWED_URLS: 'allowed_urls',
  IMAGE_URLS: 'image_urls',
  ROUTINE: 'routine',
  INQUIRY: 'inquiry',
  CHOICES: 'choices',
  CHOICE_VIDEO_URLS: 'choice_video_urls',
  PDF: 'pdf',
  UNLOCK_HABIT_PACK: 'unlock_habit_pack',
  LOG_QUESTION_DETAILS: 'log_question_details',
  LOG_QUANTITY: 'log_quantity',
  MORE_OPTION: 'more-option',
  NOTE: 'note',
  INVITE: 'invite',
  CHECK_LIST: 'check-list',
  REQUEST_USER_DETAILS: 'request-user-details',
  SKIP_ACTIVITY_MODAL: 'skip-activity-modal',
  SET_REMINDER: 'set-reminder',
  REQUEST_DATA_CONFIRMATION_MODAL: 'request-data-confirmation-modal',
  ACCOUNT_DELETION_MODAL: 'account-deletion-modal',
  FOCUS_SUMMARY_GRAPH: 'focus-summary-graph',
  EISENHOWER_MATRIX_MODAL: 'eisenhower-matrix-modal',
  ROUTINE_ACTIVITIES_STATS_MODAL: 'routine-activities-stats-modal',
  EMBEDDED_CREATE_TO_DO_MODAL: 'embedded-create-to-do-modal',
  CONFIRM_ACTION_DELETE_FOCUS_TEMPLATE: 'confirm-action-delete-focus-template',
  CONFIRM_ACTION_DELETE_HABIT_PACK: 'confirm-action-delete-habit-pack',
  CONFIRM_ACTION_REMOVE_HABIT_PACK_FROM_MARKET_PLACE:
    'confirm-action-remove-habit-pack-from-market-place',
  CONFIRM_ACTION_REMOVE_FOCUS_TEMPLATE_FROM_MARKET_PLACE:
    'confirm-action-remove-focus-template-from-market-place',
  CONFIRM_ACTION_LOGOUT: 'confirm-action-logout',
  FOCUS_SUMMARY_MORE: 'focus-summary-more',
  FOCUS_SUMMARY_EDIT: 'focus-summary-edit',
  DELETE_KEYWORD_MODAL: 'delete-keyword-modal',
  UPDATE_KEYWORD_MODAL: 'update-keyword-modal',
  PROJECT_TASKS_MODAL: 'project-tasks-modal',
  LONG_TERM_GOALS_MODAL: 'long-term-goals-modal',
  COURSE_DETAILS_MODAL: 'course-details-modal',
  SURVEY_DETAIL_MODAL: 'survey-detail-mode',
  CONFIRM_ACTION_DELETE_PLAYER_TASK: 'confirm-action-delete-player-task',
  CONFIRM_ACTION_SAVE_HABITS: 'confirm-action-save-habits',
  CONFIRM_HABIT_DELETION: 'confirm-habit-deletion',
  CONFIRM_CUSTOM_ROUTINE_DELETION: 'confirm-custom-routine-deletion',
  CONFIRM_HABIT_PACK_INSTALLATION: 'confirm-habit-pack-installation',
  CONFIRM_HABIT_PACK_UNINSTALLATION: 'confirm-habit-pack-uninstallation',
  CONFIRM_HABIT_PACK_ADD_HABIT: 'confirm-habit-pack-add-habit',
  CONFIRM_ACTION_DELETE_ACCOUNT: 'confirm-action-delete-account',
  CONFIRM_ACTION_REQUEST_DATA: 'confirm-action-request-data',
  CONFIRM_ACTION_DELETE_TASK: 'confirm-action-delete-task',
  CONFIRM_ACTION_DELETE_NOTE: 'confirm-action-delete-note',
  CONFIRM_INCLUDE_IN_EVERY_BREAK: 'confirm-include-in-every-break',
  CONFIRM_ACTION_DELETE_FOCUS_BLOCK_SUMMARY:
    'confirm-action-delete-focus-block-summary'
};

export const HABIT_SETTING_FEATURES = {
  NAME: 'name',
  EMOJI: 'habit_icon',
  DURATION: 'duration',
  COMPLETION_REQUIREMENTS: 'completion_requirements',
  LOG_QUANTITY: 'log_quantity',
  VIDEO_URLS: 'video_urls',
  LOG_QUANTITY_QUESTION: 'log_quantity_question',
  ALLOWED_APPS: 'allowed_apps',
  ALLOWED_URLS: 'allowed_urls',
  FOCUS_MODE_ID: 'allowed_focus_mode_id',
  LOG_SUMMARY_TYPE: 'log_summary_type',
  RUN_MICRO_BREAKS: 'run_micro_breaks',
  TAKE_NOTES: 'take_notes',
  PRIORITY: 'priority',
  CATEGORY: 'category',
  IMAGE_URLS: 'image_urls',
  TEXT_INSTRUCTIONS: 'text_instructions',
  DAYS_OF_WEEK: 'days_of_week',
  CURRENT_COMPETENCY_LEVEL: 'current_competency_level'
};

export const CALENDAR_RECURRENCE_RULE_FOR_DAILY_RECURRENCE =
  'FREQ=DAILY;INTERVAL=1';

export const UTC_TIMEZONE = 'UTC';

export const CALENDAR_OPTIONS = [
  { label: CalendarTypeLabels.GOOGLE, value: CalendarTypes.GOOGLE },
  { label: CalendarTypeLabels.OUTLOOK, value: CalendarTypes.OUTLOOK },
  { label: CalendarTypeLabels.YAHOO, value: CalendarTypes.YAHOO },
  {
    label: CalendarTypeLabels.MICROSOFT_CALENDAR,
    value: CalendarTypes.MICROSOFT_CALENDAR
  },
  { label: CalendarTypeLabels.ICS, value: CalendarTypes.ICS }
];

export const DEFAULT_MAC_APPS_IF_ALLOWED_URLS_SET = ['Google Chrome'];
export const DEFAULT_WINDOWS_APPS_IF_ALLOWED_URLS_SET = ['chrome.exe'];
export const HABIT_CHOICE_TYPE = {
  RANDOM: 'random',
  COMPETENCY_BASED: 'competency',
  NONE: 'none',
  CHECK_LIST: 'check_list'
};

export const LANGUAGE = {
  GB: 'GB',
  ES: 'ES'
};

export const ACTIVITIES_TABLE_COLUMNS = [
  'admin.no',
  'admin.id',
  'admin.name',
  'admin.type',
  'admin.duration_sec',
  'admin.log_summary_type',
  'admin.log_quantity_q',
  'admin.has_choices',
  'admin.log_quantity',
  'admin.is_default',
  'admin.allowed_apps',
  'admin.allowed_urls',
  'admin.video_urls',
  'admin.user_id',
  'admin.sequence_id',
  'admin.template_id',
  'admin.parent_id',
  'admin.created_at'
];

export const FOCUS_MODES_TABLE_COLUMNS = [
  'admin.no',
  'admin.id',
  'admin.name',
  'admin.user_id',
  'admin.allowed_apps',
  'admin.allowed_urls',
  'admin.created_at',
  'admin.deleted_at'
];

export const TASKS_TABLE_COLUMNS = [
  'admin.no',
  'admin.id',
  'admin.title',
  'admin.status',
  'admin.due_date',
  'admin.eisenhower_quadrant',
  'admin.duration',
  'admin.created_at',
  'admin.updated_at'
];

export const USERS_TABLE_COLUMNS = {
  PUBLIC: [
    'admin.no',
    'admin.id',
    'admin.operating_system',
    'admin.stripe_id',
    'admin.auth0_id',
    'admin.user_type',
    'admin.sign_up_pack',
    'admin.timezone',
    'admin.current_sequence',
    'admin.current_activity',
    'admin.current_focus_mode',
    'admin.last_completed_sequence',
    'admin.last_focus_mode',
    'admin.member_of_team_id',
    'admin.owner_of_team_id',
    'admin.created_at',
    'admin.subscription_status'
  ],
  PRIVATE: [
    'admin.email',
    'admin.startup',
    'admin.shutdown',
    'admin.break',
    'admin.morning_streak',
    'admin.evening_streak',
    'admin.focus_mode_streak',
    'admin.last_focus_mode',
    'admin.settings_password'
  ]
};

export const ROUTINE_SOURCE = {
  morning_activities: [
    { label: 'move_habit.break', value: ACTIVITY_TYPES.BREAKING },
    { label: 'move_habit.evening', value: ACTIVITY_TYPES.EVENING }
  ],
  break_activities: [
    { label: 'move_habit.morning', value: ACTIVITY_TYPES.MORNING },
    { label: 'move_habit.evening', value: ACTIVITY_TYPES.EVENING }
  ],
  evening_activities: [
    { label: 'move_habit.morning', value: ACTIVITY_TYPES.MORNING },
    { label: 'move_habit.break', value: ACTIVITY_TYPES.BREAKING }
  ],
  custom_activities: [
    { label: 'move_habit.morning', value: ACTIVITY_TYPES.MORNING },
    { label: 'move_habit.break', value: ACTIVITY_TYPES.BREAKING },
    { label: 'move_habit.evening', value: ACTIVITY_TYPES.EVENING }
  ]
};

export const ROUTINE_DESTINATION_POSITION = {
  START: 'start',
  MIDDLE: 'middle',
  END: 'end'
};

// Prefixes a custom routine id in the move/copy destination dropdowns so it can't collide with built-in routine keys.
export const CUSTOM_ROUTINE_OPTION_PREFIX = 'custom_';

export const USER_ROLES = {
  ADMIN: 'ADMIN',
  USER: 'USER'
};

export const TAKE_NOTE_OPTION = {
  NO_NOTE: 'NO_NOTE',
  DURING_ACTIVITY: 'DURING_ACTIVITY',
  END_OF_ACTIVITY: 'END_OF_ACTIVITY'
};

export const LOG_SUMMARY_OPTION = {
  SUMMATION: 'SUM',
  AVERAGE: 'AVG'
};

export const PRIORITY_OPTION = {
  STANDARD: 'STANDARD',
  HIGH: 'HIGH'
};

export const COUNT_DOWN_TIMER: {
  COLORS: { 0: ColorHex } & { 1: ColorHex } & ColorHex[];
  TIME: { 0: number } & { 1: number } & number[];
} = {
  COLORS: [
    COLOR.PRIMARY.LIGHT_500 as ColorHex,
    COLOR.PRIMARY.LIGHT_600 as ColorHex,
    COLOR.PRIMARY.LIGHT_700 as ColorHex,
    COLOR.PRIMARY.LIGHT_800 as ColorHex
  ],
  TIME: [24, 20, 10, 0]
};

export const ROUTINE_PLAYING_TYPE = {
  CURRENT: 'current',
  NEXT: 'next'
};

export const NORMAL_CHOICE_SELECTED_INDEX = 0;
export const MORE_OPTIONS_SETTINGS_TAB_INDEX = 2;
export const NOTIFICATION_MESSAGE_TO_DESKTOP_APP = {
  MAC: 'user-edited-settings',
  WIN: 'user-edited-settings'
};

export const LOCAL_STORAGE = {
  REDIRECT_PATH: 'redirect-path',
  FOCUS_TEMPLATE_ID: 'focus-template-id',
  PACK_ID: 'pack-id',
  AUTH_BEARER_TOKEN: 'focusbear',
  AUTH_BEARER_TOKEN_DESKTOP: 'focusbear_desktop',
  AUTH_ID: 'focusbear_id',
  AUTH_BEARER_REFRESH: 'focusbear_refresh',
  DESKTOP_SIGNIN_IN_PROGRESS: 'desktop-signin-in-progress',
  PACK_TO_INSTALL: 'pack-to-install',
  FOCUS_TEMPLATE_TO_INSTALL: 'focus-template-to-install',
  SIGN_UP_REFERRAL: 'sign-up-referral',
  AVAILABLE_PLANS: 'available-plans',
  FEATURED_HABIT_PACKS: 'featured-habit-packs',
  INVITATION_TOKEN: 'invitation-token',
  PROJECT_INVITATION_TOKEN: 'project-invitation-token',
  ERROR_PAGE_RELOADED: 'error-page-reloaded',
  TO_DO_PLAYER_TASKS: 'to-do-player-tasks',
  /** Last class session id for TodoPlayer remount anti-flash (class checklist mode). */
  TO_DO_PLAYER_CLASS_SESSION_ID: 'to-do-player-class-session-id',
  /** Prefix (per class id) for the last shared focus mode used to start a session. */
  CLASS_LAST_SHARED_FOCUS_MODE: 'class-last-shared-focus-mode',
  TO_DO_RECENT_TASKS: 'to-do-recent-tasks',
  REDIRECT_TO_MANAGE_SUBSCRIPTION_VIA_WEBVIEW:
    'redirect-to-manage-subscription-via-webview',
  SALES_IQ: 'siq_embed',
  DEVICE_ID: 'device_id',
  FEATURES_EXPRESSED_INTEREST: 'features-expressed-interest',
  ASK_SAVE_HABIT_CONFIRM: 'ask-save-habit-confirm',
  AUTH0_NONCE: 'auth_nonce',
  EMAIL_VERIFICATION_COOL_DOWN: 'cool-down',
  DID_RESET_VIDEO_URLS_FOR_ROUTINE_SUGGESTIONS_HABIT:
    'did-reset-video-urls-for-routine-suggestions',
  IS_BRAIN_DUMP_READY: 'isBrainDumpReady',
  TO_DO_FOCUS_END_TASKS: 'to-do-focus-end-tasks',
  THEME: 'theme',
  PUBLIC_ONBOARDING_DATA: 'public-onboarding-data',
  SYNC_PLATFORM_COURSES: 'sync-platform-courses',
  TEAM_ID_TO_JOIN: 'team-id-to-join',
  JOIN_CODE: 'join-code-to-use',
  WEBVIEW_BRAIN_DUMP_OPEN: 'webview-notes-brain-dump-open',
  API_RATE_LIMITED_UNTIL: 'api_rate_limited_until',
  TODO_PLAYER_TOUR_COMPLETED: 'todo-player-tour-completed'
};

export const SCREEN = {
  WIDTH: {
    SMALL: 640,
    TABLET: 768,
    LARGE: 1024,
    EXTRA_LARGE: 1280,
    DOUBLE_EXTRA_LARGE: 1536
  },
  HEIGHT: { PORTRAIT: 768, LANDSCAPE: 400 }
};

export const MORNING_ROUTINE_ADVANCED_ACTION_TYPE = {
  CREATE: 'create',
  DUPLICATE: 'duplicate'
};
/** Array.splice deleteCount values */
export const SPLICE = {
  DELETE: 1,
  CLONE: 1,
  INSERT: 0
} as const;
export const DEFAULT_DURATION = {
  MORNING: 60,
  BREAKING: 60,
  EVENING: 600,
  LIBARARY: 600,
  STANDALONE: 60,
  DEFAULT: 60,
  MORE_OPTION: 60
};
export const COUNT_DOWN_TIMER_SIZE = {
  DEFAULT: 180,
  LANDSCAPE: 115
};

export const INQUIRY_NOTICE_MODAL_SWITCH_SIZE = {
  WIDTH: 50,
  HEIGHT: 20
};
export const INQUIRY_TYPE = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
  CALENDAR: 'calendar',
  DESKTOP_APP: 'desktop_app'
};

export const ALLOWED_APPS_MIN_QUERY_LENGTH = 1;
export const TEMPLATES_TEXT_MAX_CHARACTER_LENGTH = 2500;
export const OPEN_STRIPE_CHECKOUT = 'open-stripe-checkout';
export const ROUTINE_SUMMARY_STAT = {
  MIN: 0,
  MAX: 30
};
export const REACT_SELECT_DROPDOWN = {
  Z_INDEX: {
    DEFAULT: 60,
    MOVE_ACTIVITY: 60,
    NEW_TASK: 71,
    SUB_TASKS: 99,
    CHOICE: 999,
    CUSTOM_ROUTINE: 9999
  },
  FONT_SIZE: 12
};

export const DEFAULT_CHOICE_OPTIONS = [
  { label: HABIT_CHOICE_TYPE.NONE, value: HABIT_CHOICE_TYPE.NONE },
  { label: HABIT_CHOICE_TYPE.RANDOM, value: HABIT_CHOICE_TYPE.RANDOM },
  // DESCRIPTION: Deon informed that it needs to be disable for a while
  // { label: 'competency_based', value: HABIT_CHOICE_TYPE.COMPETENCY_BASED },
  { label: HABIT_CHOICE_TYPE.CHECK_LIST, value: HABIT_CHOICE_TYPE.CHECK_LIST }
];

/** `label` is an i18n key — translate with `t(label)` at render. */
export const ROUTINE_CHOICE_OPTIONS = [
  { label: HABIT_CHOICE_TYPE.NONE, value: HABIT_CHOICE_TYPE.NONE },
  // DESCRIPTION: Deon informed that it needs to be disable for a while
  // { label: 'competency_based', value: HABIT_CHOICE_TYPE.COMPETENCY_BASED },
  { label: HABIT_CHOICE_TYPE.CHECK_LIST, value: HABIT_CHOICE_TYPE.CHECK_LIST }
];

export const RICH_TEXT_EDITOR_HEIGHT = {
  DEFAULT: 90,
  NEW_TASK: 120,
  ROUTINE: 350,
  CREATE_NOTE: 100,
  BRAIN_DUMP: 100
};

/**
 * Note editor height in the Edit/Create Note drawer. Viewport-relative so the
 * editor fills the available space instead of the cramped fixed 100px, while
 * staying usable on short webview screens (min) and not overwhelming tall ones (max).
 */
export const NOTE_EDITOR_HEIGHT = 'clamp(200px, 45vh, 520px)';
export const SPINNER_TITLE = {
  INSTALL: 'installing',
  UPDATE: 'updating'
};
export const CLONE_ICON = {
  HABIT_PACKS: {
    WIDTH: 12,
    HEIGHT: 12
  }
};
export const EMPTY_STRING = '';
export const EMPTY_TASKS_JSON = '[]';
export const HABIT_PACK_FOCUS_TEMPLATES_TAB_INDEX = {
  HABIT_PACKS: 0,
  FOCUS_TEMPLATES: 1
};
export const MARKET_TAB_INDEX = {
  HABIT_PACKS: 0,
  FOCUS_TEMPLATES: 1
};

export const HTTP_STATS_CODE = {
  NO_CONNECTION: 0,
  SUCCESS: 200,
  NO_CONTENT: 204,
  CREATED: 201,
  NOT_FOUND: 404,
  INTERNAL_SEVER_ERROR: 500,
  BAD_REQUEST: 400,
  TOO_MANY_REQUEST: 429,
  REST_CONTENT: 205,
  FORBIDDEN: 403,
  UNAUTHORIZED: 401,
  PAYMENT_REQUIRED: 402,
  CONFLICT: 409
};

export const COMPETENCY_CHOICES_LEVEL_THRESHOLDS = {
  LEVEL_DOWN: 4,
  LEVEL_UP: 9
};
export const NUMBER_OF_ACTIVITIES_SHOULD_SHOW_ADD_NEW_ACTIVITY_TO_EACH_ACTIVITY = 10;
export const FOCUS_MODE_ALLOWED_TYPE = {
  APPS: 'apps',
  URLS: 'urls'
};

export const LANGUAGE_OPTIONS = [
  { value: LanguageCode.EN, label: Language.ENGLISH },
  { value: LanguageCode.ES, label: Language.SPANISH }
];

export const PDF_FILENAME = {
  SETTINGS: 'settings.pdf'
};
export const PACK_OPTIONS = {
  ROUTINE: 'routine',
  STANDALONE: 'standalone'
};
export const COOKIES = {
  LANGUAGE: 'i18next'
};
export const SHORT_DAY_NAME = 'ddd';
export const PDF_PAPER_TYPE = 'A4';
export const FOCUS_BEAR = 'Focus Bear';
export const ITEM_INDEX_NOT_FOUND = -1;
export const INITIAL_COMPETENCY_LEVEL = 1;
export const MAXIMUM_IMAGE_URLS = 4;
export const ACTIVITY_TEMPLATE_ID = 'activity_template_id';

export const SENTRY = {
  DNS: 'https://8b33ad393d38fc72d06eab2fd40446be@o4509122919006208.ingest.us.sentry.io/4509122945810432',
  TRACE_SAMPLE_RATE: 0.2,
  REPLAYS_SESSION_SAMPLE_RATE: 0,
  REPLAYS_ON_ERROR_SAMPLE_RATE: 1.0
};
export const LOG_QUANTITY_QUESTION_FEATURES = {
  QUESTION: 'question',
  QUESTION_DETAILS: 'question_details'
};
export const OK = 'ok';
export const ACTIVITY_SEQUENCE_TYPE = {
  MORNING: 'morning',
  EVENING: 'evening',
  BREAKING: 'breaking',
  STANDALONE: 'standalone'
};

export const TOAST = {
  COUNT: {
    DEFAULT: 1
  },
  AUTO_CLOSE: {
    DEFAULT: 6000,
    COPIED: 1000,
    ONBOARDING_RESUME: 5000,
    TASK_COMPLETED_BANNER: 3000
  },
  LIMIT: 1
};
export const QUOTE = '"';
export const FORWARD_SLASH = '/';
export const FONTS = {
  SANS: 'font-sans',
  SEGOE_UI: 'font-segoeUI',
  SAN_FRANCISCO: 'font-sanFrancisco',
  OPEN_DYSLEXIC: 'font-openDyslexic',
  ARIAL: 'font-arial',
  COMIC_SANS: 'font-comicSans',
  VERDANA: 'font-verdana',
  TAHOMA: 'font-tahoma',
  HELVETICA: 'font-helvetica',
  INTEL_ONE_MONO: 'font-intelOneMono',
  CENTURY_GOTHIC: 'font-centuryGothic',
  ROBOTO: 'font-roboto',
  TREBUCHET_MS: 'font-trebuchetMS',
  POPPINS: 'font-poppins'
};

export const ACTIVITY_FILTER_OPTIONS = [
  'MORNING',
  'BREAKING',
  'EVENING',
  'STANDALONE',
  'LIBRARY'
];
export const PAGINATE_ITEMS = 20;
export const SEARCH_RESULT = {
  ACTIVITIES: 'activities',
  FOCUS_MODES: 'focus-modes',
  NONE: EMPTY_STRING
};
export const SEPARATOR = {
  COMMA: ', '
};

export const QUESTION_MARK = '?';
export const LEVEL_KEYS = [
  'novice',
  'apprentice',
  'explorer',
  'disciplined',
  'diligent',
  'strategist',
  'mentor',
  'scholar',
  'guardian',
  'achiever'
];

export const ADMIN_USER_TABLE_FIELD = {
  REVENUE_CAT_STATUS: 'revenue_cat_status'
} as const;

export const PERSPIRATION_LEVEL = {
  LOW: 'low',
  FULL: 'full'
} as const;

export const PRIVATE_USER_DETAILS_KEYS = [
  'email',
  'startup_time',
  'shutdown_time',
  'break_after_minutes',
  'morning_routines_streak',
  'evening_routines_streak',
  'focus_modes_streak',
  'is_office_mode_activated',
  'has_edited_settings'
];

export const PUBLIC_USER_DETAILS_KEYS = [
  'id',
  'local_device_settings',
  'stripe_customer_id',
  'auth0_id',
  'user_type',
  'signed_up_via_habit_pack',
  'timezone',
  'current_activity_sequence_id',
  'current_activity_id',
  'current_focus_mode_id',
  'last_completed_sequence_at',
  'last_completed_focus_mode_at',
  'member_of_team_id',
  'owner_of_team_id',
  'created_at',
  'revenue_cat_status'
];

export const USER_FOCUS_MODE_KEYS = [
  'id',
  'name',
  'user_id',
  'allowed_apps',
  'allowed_urls',
  'created_at',
  'deleted_at'
];

export const USER_ACTIVITIES_KEY = [
  'id',
  'type',
  'duration_seconds',
  'log_summary_type',
  'has_choices',
  'log_quantity',
  'is_default',
  'user_id',
  'activity_sequence_id',
  'activity_template_id',
  'parent_id',
  'created_at',
  'log_quantity_question',
  'name',
  'video_urls',
  'allowed_apps',
  'allowed_urls'
];

export const HABIT_SKIP_REASON = {
  ALREADY_DID_IT: 'already-did-it',
  CAN_NOT_DO_IT: 'can-not-dot-it'
};

export const TIMEZONE = {
  UTC: 'utc'
};

export const FILENAME = {
  FOCUS_SUMMARY: 'focus_summary.csv',
  EISENHOWER_MATRIX_SUMMARY: 'eisenhower_matrix_summary.csv',
  TO_DO_TASKS_SUMMARY: 'to_do_tasks_summary.csv'
};

export const HABIT_PACK_OPTION = {
  INSTALLED: 'installed',
  FEATURED: 'featured'
};

export const REFERRAL = {
  HABIT_PACK: 'habit-pack',
  FOCUS_TEMPLATE: 'focus-template',
  NONE: 'none'
};

export const USER_SETTINGS_KEYS = {
  HAS_EDITED_SETTINGS: 'has_edited_settings',
  CUTOFF_TIME_FOR_NON_HIGH_PRIORITY_ACTIVITIES:
    'cutoff_time_for_non_high_priority_activities'
};

export const FOCUS_SUMMARY = {
  GRAPH_TENSION: 0.25,
  TIME_UNIT: {
    SECOND: 'second',
    DAY: 'day',
    MONTH: 'month',
    YEAR: 'year'
  } as const,
  COLUMN: {
    DURATION: 0,
    START_TIME: 1,
    FINISH_TIME: 2,
    INTENTION: 3,
    DISTRACTION: 4,
    FOCUS_BLOCK: 5
  },
  SUMMARY_OPTION: {
    DAILY: 'daily',
    MONTHLY: 'monthly',
    YEARLY: 'yearly'
  }
};

export const DYNAMIC_PATH_ID = ':id';
export const SKIP_ACTIVITY_COUNT_DOWN_OFFSET = 15;
export const STRIPE_CUSTOMER_ID_CHARACTER = 18;
export const FOCUS_SUMMARY_DATA_LIMIT = 30;
export const FOCUS_LOG_PAGE_SIZE = 50;
export const SKIP_ACTIVITY_COUNT_DOWN_INTERVAL = 1000;

export const EISENHOWER_PRIORITY_TITLE_ORIENTATION = {
  VERTICAL: 'vertical',
  HORIZONTAL: 'horizontal'
};

export const EISENHOWER_QUADRANT = {
  DO: '1',
  DECIDE: '2',
  DELEGATE: '3',
  DELETE: '4'
};

export const OPEN_AI_RESPONSE_DATA = {
  DONE: '[DONE]',
  PROMPT: 'PROMPT:'
};

export const ROUTINE_ACTIVITIES_STATS_OPTIONS = [
  'routine_player.daily',
  'routine_player.time_range'
];

export const ROUTINE_ACTIVITIES_STATS_KEY = {
  DAILY: 0,
  TIME_RANGE: 1
};

export const DEFAULT_ACTIVITY_STATS_DAYS = 30;
export const INITIAL_RATING = 0;

export const EISENHOWER_QUADRANT_OPTIONS: DropDownSelectedValue[] = [
  {
    label: t('to_do_procrastinate.urgent_and_important'),
    value: EISENHOWER_QUADRANT.DO
  },
  {
    label: t('to_do_procrastinate.important_but_not_urgent'),
    value: EISENHOWER_QUADRANT.DECIDE
  },
  {
    label: t('to_do_procrastinate.not_important_and_urgent'),
    value: EISENHOWER_QUADRANT.DELEGATE
  },
  {
    label: t('to_do_procrastinate.not_important_and_not_urgent'),
    value: EISENHOWER_QUADRANT.DELETE
  }
];

/** `label` is an i18n key — translate with `t(label)` at render. */
export const IMPACT_MEASUREMENT_OPTIONS = [
  {
    label: 'impact_measurement.hours_of_sleep',
    value: 'hours_of_sleep'
  },
  {
    label: 'impact_measurement.energy_level_upon_awakening',
    value: 'energy_level_upon_awakening'
  },
  { label: 'impact_measurement.mood', value: 'mood' },
  {
    label: 'impact_measurement.perception_of_productivity',
    value: 'perception_of_productivity'
  }
];

export const TIME_UNIT_CONVERT_FACTOR = {
  ONE_SECOND_AS_MILLISECONDS: 1000,
  ONE_MINUTE_AS_SECONDS: 60,
  ONE_HOUR_AS_SECONDS: 60 * 60,
  ONE_DAY_AS_SECONDS: 60 * 60 * 24,
  ONE_DAY_AS_MILLISECONDS: 60 * 60 * 24 * 1000,
  ONE_WEEK_AS_DAYS: 7,
  ONE_MONTH_AS_DAYS: 30,
  ONE_YEAR_AS_DAYS: 365,
  TWO_YEAR_AS_DAYS: 365 * 2,
  ONE_HOUR_AS_MINUTES: 60
};

/** Matches default duration in `DEFAULT_TO_DO_PLAYER_TASK` (5 minutes). */
export const DEFAULT_PLAYER_QUICK_ADD = {
  DURATION: TIME_UNIT_CONVERT_FACTOR.ONE_MINUTE_AS_SECONDS * 5
};

export const DESKTOP_APP_CLOSE = {
  SURVEY_MESSAGE: 'close-survey-ui',
  TO_DO_TASK_UI: 'close-to-do-task-ui'
};

export const MAC_APP_DATA_LOCATION = {
  START_FOCUS_MODE: 'start-focus-mode',
  END_FOCUS_MODE: 'end-focus-mode',
  CLOSE_SURVEY_UI: 'close-survey-ui',
  CLOSE_TODO_TASK_UI: 'close_todo_task_ui',
  SUBMIT_USER_SETTINGS: 'submit-user-settings',
  USER_SETTINGS_UPDATED: 'user-settings-updated',
  SEND_NEW_TASK_FROM_TO_DO_PLAYER: 'send-new-task-from-todo-player',
  SEND_SUGGESTED_ACTIVITIES: 'send-suggested-activities',
  SEND_SELECTED_HABIT_PACK_ID: 'send-selected-habit-pack-id',
  SEND_SELECTED_HABIT_PACK: 'send-selected-habit-pack',
  SEND_USER_SUBSCRIPTION_STATUS: 'send_user_subscription_status',
  EDIT_HABITS_ENABLED: 'edit-habits-enabled',
  ROUTINE_SUGGESTIONS_STEP: 'routine-suggestions-step',
  SEND_INTENTION: 'send-intention',
  REQUEST_ACCESS_TOKEN: 'request-access-token'
};

export const SETTING_TAB_INDEX = {
  TIMING: 0,
  MORNING_ROUTINE: 1,
  MICRO_BREAKS: 2,
  EVENING_ROUTINE: 3,
  CUSTOM_ROUTINES: 4,
  HABIT_PACKS: 5
};

export const NUMBER_OF_MONTHS = 1;
export const FIRST_ACTIVITY_INDEX = 0;

export const INTEGRATION_PLATFORM = {
  ZOHO: 'Zoho',
  JIRA: 'Jira',
  CLICK_UP: 'ClickUp',
  TRELLO: 'Trello',
  ASANA: 'Asana',
  MONDAY: 'Monday',
  GOOGLE: 'Google',
  MICROSOFT: 'Microsoft'
};

export const TO_DO_TAB = {
  TO_DO: 0,
  PROCRASTINATE: 1,
  PROJECTS: 2,
  INTEGRATIONS: 3
};

export const TO_DO_PAGE_TAB = {
  FOCUS: 'focus',
  PROCRASTINATE: 'procrastinate'
} as const;

export const ACTIVITY_STATS_ROUTINE = {
  ALL: 'all',
  MORNING: 'morning',
  BREAK: 'break',
  EVENING: 'evening'
} as const;

export const COURSE_PLATFORM = {
  MOBILE: 'MOBILE'
} as const;

export const COURSE_TAG = {
  MOBILE: 'mobile'
} as const;

/** tabLayout.activeTab value for the completed-tasks view in the tasks toolbar. */
export const COMPLETED_TAB = 1;

export const INITIAL_TEXT_EDITOR_VALUE = '<p></p>\n';
export const NUMBER_OF_DIGITS = 1;

export const EMBEDDED_EXPLAINER_VIDEOS = {
  SETUP_ALWAYS_BLOCKED_URLS: 'https://youtu.be/3U3ktaodAzk',
  INSTALL_MOBILE_APPLICATION: 'https://youtube.com/shorts/zv6be2sNOsk',
  SET_UP_A_FOCUS_MODE: 'https://youtu.be/L0ksKXqOXpk',
  EDIT_YOUR_HABITS: 'https://youtu.be/BCVdt210PGA',
  CHAT_WITH_FOCUS_BEAR: 'https://youtube.com/shorts/zfhQOxIFOZg'
};

export const TEAM_NAME_PLACEHOLDER = 'Team 1';

export const LOAD_PROJECT_OPTIONS_DELAY = 700;
export const USERNAME_PLACEHOLDER = '-----';
export const NUMBER_OF_DECIMAL_PLACES = 2;

export const PAGE = {
  INIT_TOTAL: 1,
  FIRST: 1,
  TAKE: {
    DEFAULT: 50,
    NOTES: 10,
    DAY_PLAN_TASK_CATALOG: 30,
    PROJECT_NOTE: 30
  }
};

export const TAB_INDEX = {
  DEFAULT: 0,
  SKIP: -1,
  HIGH_PRIORITY: 1
} as const;

export const DEFAULT_TEAM = 0;
export const FIRST_QUESTION_INDEX = 0;
export const DEFAULT_TAB_LAYOUT_TAB = 0;
export const ERROR_PAGE_RELOADED = 'true';
export const DELETE_COUNT = 1;
export const NO_ELEMENT_FOUND = -1;
export const FIRST_LOG_QUESTION_INDEX = 0;
export const AUTO_COMPLETION_TAGS = {
  DELETE_OLD_POSITION_TAG_COUNT: 1,
  DELETE_NEW_POSITION_TAG_COUNT: 0
};
export const FIRST_DEVICE_INFO_INDEX = 0;
export const PLATFORM = ['Android', 'Windows', 'iOS', 'MacOS'];

export const ROUTINE_PLAYER_FEATURE = {
  ACTIVITY_CATEGORIES: 1,
  ROUTINE_ACTIVITIES: 2,
  ACTIVITY_PLAYER: 3,
  ACTIVITY_SUMMARY: 4
};
export const MINIMUM_NUMBER_OF_MEMBER = 1;
export const REDIRECTION_COMPLETED = '1';
export const MAXIMUM_NUMBER_OF_CHARACTERS = 80;
export const PROFILE_IMAGE = {
  FIRST_IMAGE_INDEX: 0,
  MAXIMUM_FILES: 1,
  MAXIMUM_FILE_SIZE: 1000000, // number of bytes => 1MB
  FILE_TYPES: ['jpg', 'png']
};
export const PULL_TO_REFRESH = {
  PULL_DOWN_TO_REFRESH: 10,
  MAXIMUM_PULL_DOWN_DISTANCE: 60
};
export const TEAM_PAYMENT = {
  STRIPE: 'stripe',
  OFFLINE: 'offline'
};
export const MINIMUM_NUMBER_OF_PAYMENT_METHODS = 1;
export const PLATFORM_INDEX = 1;
export const CALENDER_PLATFORMS = [
  INTEGRATION_PLATFORM.GOOGLE,
  INTEGRATION_PLATFORM.MICROSOFT
];
export const FOCUS_TEMPLATE_FEATURE = {
  NAME: 'name',
  DESCRIPTION: 'description',
  DESCRIPTION_VIDEO_URL: 'description_video_url',
  WELCOME_MESSAGE: 'welcome_message',
  WELCOME_VIDEO_URL: 'welcome_video_url',
  AUTHOR_NAME: 'author_name',
  ALLOWED_URLS: 'allowed_urls',
  ALLOWED_APPS: 'allowed_apps',
  IS_FEATURED: 'is_featured',
  FEATURE_FOR_ONBOARDING: 'featured_for_onboarding',
  MARKETPLACE_APPROVAL_STATUS: 'marketplace_approval_status'
};

export const ASSIGN_OPTIONS: DropDownSelectedValue[] = [
  {
    label: t('only_sync_assigned_tasks'),
    value: '1'
  },
  {
    label: t('sync_all_tasks'),
    value: '2'
  }
];

export const DEFAULT_ASSIGN_STATUS = [
  {
    platform: INTEGRATION_PLATFORM.ZOHO,
    status: '1'
  },
  {
    platform: INTEGRATION_PLATFORM.CLICK_UP,
    status: '1'
  },
  {
    platform: INTEGRATION_PLATFORM.ASANA,
    status: '1'
  },
  {
    platform: INTEGRATION_PLATFORM.JIRA,
    status: '1'
  },
  {
    platform: INTEGRATION_PLATFORM.MONDAY,
    status: '1'
  },
  {
    platform: INTEGRATION_PLATFORM.TRELLO,
    status: '1'
  }
];

export const ROUTINE = {
  MORNING: 'morning',
  EVENING: 'evening',
  BREAKING: 'breaking',
  SUGGESTION: 'suggestion',
  CUSTOM_ROUTINES: 'custom_routines'
};
export const MOVE_OPTION = {
  TOP: 'top',
  BOTTOM: 'bottom'
};
export const ROUTINE_FIRST_ACTIVITY_INDEX = 0;
export const NUMBER_OF_DELETE_ITEMS = 1;
export const NUMBER_OF_WEEK_DAYS = 7;
export const TIMING_SUMMARY_TIMELINE_ITEMS_PER_ROW = {
  TABLET: 2,
  DESKTOP: 4
};
export const DEFAULT_ACTIVITY_CUTOFF = {
  TIME: '21:00',
  OFFSET: 30
};

export const CUTOFF_TIME_AFTER_SHUTDOWN_OFFSET_MINUTES = 1;
export const FIRST_LESSON_OFFSET = 0;
export const DEFAULT_VIDEO_DURATION = {
  playedSeconds: 0,
  totalSeconds: 0
};
export const SEEK_TO_START = 0;
export const TASK_PROJECT = {
  MAXIMUM_NUMBER_OF_CHARACTERS: 12,
  MAXIMUM_NUMBER_OF_TASKS: 2
};
export const SORT_VALUE = {
  LEFT_LESS_THAN_RIGHT: -1,
  LEFT_GREATER_THAN_RIGHT: 1
};
export const LEADER_BOARD_COLUMN = {
  RANK: 0
};
export const EXPORT_TO_DO_TASKS_EMPTY_PLACEHOLDER = '--';
export const NUMBER_OF_STARS = 5;
export const COURSE_FEEDBACK = {
  MAX_LENGTH: 500,
  ROWS: 4
};
export const FIRST_TEAM_INDEX = 0;
export const MORE_OPTION_TAB = {
  TIMING: 'timing',
  VIDEOS: 'settings_page.videos',
  ALLOWED_APPS: 'focus_mode.allowed_apps',
  LOG_DATA: 'log_data',
  SETTINGS: 'tabs_revamp.activity_choices_tab.sub_habits',
  ADVANCED: 'advanced',
  INSTRUCTIONS: 'instructions'
};
export const MINIMUM_CHARACTERS_CANCEL_REASON = 10;
export const MAXIMUM_TO_DO_DETAILS_CHARACTERS = 3200;
export const MAXIMUM_TO_DO_OBJECTIVE_CHARACTERS = 3200;
export const NUMBER_OF_GOALS_HEADERS = 9;
export const NUMBER_OF_VISIBLE_LEVELS = 7;
export const GOALS_LEVEL = {
  start: 0,
  level_1: 1,
  level_2: 2,
  level_3: 3,
  level_4: 4,
  level_5: 5,
  level_6: 6,
  level_7: 7,
  level_8: 8,
  level_9: 9
};
export const GOALS_LEVEL_TARGET = {
  [GOALS_LEVEL.level_1]: {
    [t('stats_page.morning_routine_streak')]: 1,
    [t('stats_page.micro_break_streak')]: 1,
    [t('stats_page.evening_routine_streak')]: 1,
    [t('stats_page.focus_mode_streak')]: 1
  },
  [GOALS_LEVEL.level_2]: {
    [t('stats_page.morning_routine_streak')]: 2,
    [t('stats_page.micro_break_streak')]: 2,
    [t('stats_page.evening_routine_streak')]: 2,
    [t('stats_page.focus_mode_streak')]: 2
  },
  [GOALS_LEVEL.level_3]: {
    [t('stats_page.morning_routine_streak')]: 5,
    [t('stats_page.micro_break_streak')]: 5,
    [t('stats_page.evening_routine_streak')]: 5,
    [t('stats_page.focus_mode_streak')]: 5,
    [t('stats_page.chat_with_focus_bear')]: 1,
    [t('stats_page.setup_a_focus_mode')]: 1
  },
  [GOALS_LEVEL.level_4]: {
    [t('stats_page.morning_routine_streak')]: 7,
    [t('stats_page.micro_break_streak')]: 7,
    [t('stats_page.evening_routine_streak')]: 7,
    [t('stats_page.focus_mode_streak')]: 10
  },
  [GOALS_LEVEL.level_5]: {
    [t('stats_page.morning_routine_streak')]: 10,
    [t('stats_page.micro_break_streak')]: 10,
    [t('stats_page.evening_routine_streak')]: 10,
    [t('stats_page.focus_mode_streak')]: 15
  },
  [GOALS_LEVEL.level_6]: {
    [t('stats_page.morning_routine_streak')]: 15,
    [t('stats_page.micro_break_streak')]: 15,
    [t('stats_page.evening_routine_streak')]: 15,
    [t('stats_page.focus_mode_streak')]: 30
  },
  [GOALS_LEVEL.level_7]: {
    [t('stats_page.morning_routine_streak')]: 30,
    [t('stats_page.micro_break_streak')]: 30,
    [t('stats_page.evening_routine_streak')]: 30,
    [t('stats_page.focus_mode_streak')]: 50
  },
  [GOALS_LEVEL.level_8]: {
    [t('stats_page.morning_routine_streak')]: 60,
    [t('stats_page.micro_break_streak')]: 60,
    [t('stats_page.evening_routine_streak')]: 60,
    [t('stats_page.focus_mode_streak')]: 100
  },
  [GOALS_LEVEL.level_9]: {
    [t('stats_page.morning_routine_streak')]: 100,
    [t('stats_page.micro_break_streak')]: 100,
    [t('stats_page.evening_routine_streak')]: 100,
    [t('stats_page.focus_mode_streak')]: 200
  }
};

export const GOALS_REQUIREMENT_WITHOUT_COUNTER = [
  t('stats_page.chat_with_focus_bear'),
  t('stats_page.setup_a_focus_mode')
];

export const TO_DO_PLAYER = {
  MINIMUM_DURATION: 1,
  EMOJI_SIZE: {
    DEFAULT: 20,
    NEW_TASK: 30
  },
  TASK_COUNT: {
    UNINITIALIZED: -1
  },
  FIRST_TASK_INDEX: 0,
  NUMBER_OF_TASK_TO_DELETE: 0,
  MINIMUM_REMAINING_SECONDS: 0,
  EMBEDDED_MAX_WIDTH_PX: 60,
  TOUR_PROXY_MEASURE_DELAY_MS: 500,
  TOUR_PROXY_MIN_WIDTH: { DURATION: 84, PLAY: 32 },
  TOUR_PROXY_MIN_HEIGHT: { DURATION: 24, PLAY: 32 },
  TOUR_VIEWPORT_PADDING_PX: 4,
  QUICK_ADD_STATIC_TASK_ID: 'quick-add-static',
  TAB_INDEX: {
    PLAYER: 0,
    TODO_LIST: 1
  }
};

export const DEFAULT_TODO_TASK_DURATION = 60;
export const DEFAULT_TO_DO_TASK_EMOJI = '1f600';
export const MCP_AGENT_EMOJI_CODE = '1f916';
export const MCP_AGENT_EMOJI = '\u{1F916}';
/** Hex unified codes from the API (e.g. `1f600`, `1f468-200d-1f4bb`). */
export const HEX_EMOJI_CODE_PATTERN =
  /^[0-9a-fA-F]{4,6}(?:-[0-9a-fA-F]{4,6})*$/;
export const TO_DO_COMPLETED_STATUS = 2;
export const EMPTY_TASKS = 0;

export const EMPTY_LIST = 0;

/** Discriminator for client-side async result unions (fulfilled / rejected). */
export const ASYNC_RESULT_STATUS = {
  FULFILLED: 'fulfilled',
  REJECTED: 'rejected'
} as const;

export type AsyncResultStatus =
  (typeof ASYNC_RESULT_STATUS)[keyof typeof ASYNC_RESULT_STATUS];

export const YOUTUBE_URL_TAB_INDEX = 1;

export const DEFAULT_FEEDBACK_CHOICES = 11;
export const DEFAULT_HABIT_DURATION = 300;

export const TO_DO_PLAYER_SEARCH_DEBOUNCE_DELAY = 500;
export const FIRST_SUGGESTION_INDEX = 0;
export const FIRST_SETTING_TAB_INDEX = 0;
export const TO_DO_SUB_TASK_WHERE = {
  TO_DO_LIST: 'to-do-list',
  TO_DO_NEW_TASK_FORM: 'to-do-new-task-form',
  TO_DO_PLAYER: 'to-do-player'
};
export const FOCUS_END_DURATION_NUMBER_OF_DECIMAL_PLACES = 1;
/** Hours below this threshold are shown as minutes in focus duration labels. */
export const FOCUS_DURATION_THRESHOLD = {
  DEFAULT: 1
} as const;
export const DEFAULT_NEW_TODO_TASK_DURATION = 600;
export const PROCRASTINATE_NOTE_MAXIMUM_NUMBER_OF_CHARACTERS = 255;

export const TO_DO_PLAYER_TASK_BTN_DURATION = {
  INCREMENT_A_MINUTE: 'increment-a-minute',
  INCREMENT_TEN_MINUTE: 'increment-ten-minute',
  DECREMENT_A_MINUTE: 'decrement-a-minute',
  DECREMENT_TEN_MINUTE: 'decrement-ten-minute',
  MANUAL: 'manual'
};
export const MORNING_ROUTINE_TAB_INDEX = 0;
export const EVENING_ROUTINE_TAB_INDEX = 2;

export const REACT_KEY = {
  MORNING_ROUTINE_SUGGESTION: '8bf7044c-235e-43ff-8464-2f53895ac3ab',
  EVENING_ROUTINE_SUGGESTION: 'fc6a6c9a-999d-47fd-911c-2e8a3e53e5ab',
  MORNING_ROUTINE_SUGGESTION_ICON: 'e62cad0d-162e-4a6c-b9cb-6a29c4ff4a52',
  EVENING_ROUTINE_SUGGESTION_ICON: '794948ca-e3e1-4fa3-9f87-1645268e584b'
};

export const ENROLLED_COURSES_TAB = {
  title: t('course.enrolled_courses'),
  index: 0
};

export const OTHER_COURSES_TAB = {
  title: t('course.other_courses'),
  index: 1
};
export const DEFAULT_TO_DO_PLAYER_DURATION = 10;
export const DEFAULT_SURVEYS_PER_PAGE = 20;
export const DEFAULT_TASKS_PER_PAGE = 50;

// Used when exporting tasks to CSV: fetch all pages, with a safety cap on how
// many pages we will request. NOTE: the /to-do API rejects `take` > 50 (HTTP
// 400), so this must stay <= 50 or every export request fails and silently
// falls back to the currently-loaded page.
export const EXPORT_TASKS = {
  MAX_PAGES: 400,
  PER_PAGE: 50
};

export const TO_DO_SEARCH_DEBOUNCE_DELAY = 500;
export const MAC_APP_DATA_MESSAGE = {
  ACTIVE_SUBSCRIPTION: 'The user subscriptions status is active',
  INACTIVE_SUBSCRIPTION: 'The user subscriptions status is inactive',
  EDIT_HABITS_ENABLED: 'Edit habits enabled',
  ROUTINE_SUGGESTIONS_INSTALLED_SUCCESSFULLY:
    'routine-suggestions-installed-successfully'
};
export const TO_DO_PLAYER_EXTEND_TASK_TIME = 5; //@Description: the unit is minute
export const SECOND_SUBTASK_POSITION = 2;
export const ROUTINE_SUGGESTION_WIZARD_STEP = {
  INTRO: 0,
  GOALS: 1,
  ROUTINES: 2,
  HABITS: 3
};
export const ROUTINE_INTRODUCTION_YOUTUBE_VIDEO =
  'https://www.youtube.com/watch?v=ZRbrfTWa8AI';

export const DEFAULT_TO_DO_PLAYER = {
  intention: EMPTY_STRING,
  isReadyToSendUpdates: false,
  tasks: [],
  isSelectedTaskCompleted: false,
  completedTask: null,
  focusAlignmentScore: undefined,
  isSearchingToDos: false,
  tasksPlayedIds: [],
  searchResults: null,
  showPlayerNewTaskModal: false,
  brainDump: '',
  brainDumpModalConvertedTasks: [],
  isBrainDumpReady: false,
  focusModeEndTime: undefined,
  isPausedFromDesktop: false,
  focusModePauseStartedAtMs: null,
  focusModeAccumulatedPauseOffsetMs: 0,
  isSyncingFromDesktop: false,
  topScoreDrawerTaskId: null,
  classSessionId: null,
  isPlayerMetaResolved: false,
  selectedTabIndex: TO_DO_PLAYER.TAB_INDEX.PLAYER
};

export const CHAT_WINDOW_MOBILE_MIN_WIDTH = 555;
export const ALL_APPS_POST_MESSAGE_DATA = {
  ROUTINE_SUGGESTIONS_INSTALLED_SUCCESSFULLY:
    'routine-suggestions-installed-successfully',
  ROUTINE_SUGGESTIONS_INSTALLATION_FAILED:
    'routine-suggestions-installation-failed'
};

export const LEADER_BOARD_DATA = {
  MORNING_STREAK: 'Morning Streak',
  BREAK_STREAK: 'Break Streak',
  EVENING_STREAK: 'Evening Streak',
  FOCUS_STREAK: 'Focus Streak'
};

export const LEADER_BOARD_DETAIL_METRIC_ID = {
  MORNING: 'morning',
  BREAK: 'break',
  EVENING: 'evening',
  FOCUS: 'focus'
} as const;

export const DEBOUNCE_DELAY = {
  DEFAULT: 200,
  ADMIN_TEAMS_SEARCH: 400,
  CANDIDATE_SEARCH: 400,
  TO_DO_SEARCH: 500,
  MARKET_PLACE_SEARCH: 300,
  ACTIVITY_SUGGESTION_SEARCH: 300,
  NOTE_SEARCH: 500
};

export const DAYS_IN_90_DAY_PERIOD = 90;

export const CUSTOM_ROUTINE_FEATURE = {
  NAME: 'name',
  START_TIME: 'start-time',
  END_TIME: 'end-time',
  TRIGGER: 'trigger',
  DAYS: 'days'
};

export const USE_DEBOUNCE_RESIZE_DELAY = {
  DEFAULT: 200
};

export const TIME_FORMAT_24H = 'HH:mm';
export const TIME_FORMAT_12H = 'hh:mm A';
/** 12h clock without a leading zero on the hour (e.g. "5:27 PM"). */
export const TIME_FORMAT_12H_SHORT = 'h:mm A';

/** Strict 24h clock regexes for validating time-of-day strings. */
export const LOCAL_TIME_PATTERN = {
  HM: /^([01]\d|2[0-3]):[0-5]\d$/,
  HMS: /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/
} as const;

export const DEFAULT_INVALID_FOCUS_END_TASK_INDEX = 0;
export const BRAIN_DUMP_CHAR_LIMIT = 1000;
export const BRAIN_DUMP_TIMEOUT = {
  SHORT_LENGTH: 300,
  MEDIUM_LENGTH: 700,
  MEDIUM_TIMEOUT: 45000,
  LONG_TIMEOUT: 60000
};
export const ERROR = {
  CHUNK_LOAD: 'ChunkLoadError'
};

export const SINGLE_ENTITLEMENT = 1;
export const ACTIVITY_SUGGESTIONS_SEARCH_THRESHOLD = 2;

export const EMAIL_VERIFICATION_COOL_DOWN = {
  START: 0,
  RESET: 120
};

export const API_RATE_LIMIT = {
  RESPONSE_HEADER: 'retry-after',
  TOAST_ID: 'api_rate_limited_toast',
  COUNTDOWN_TICK_INTERVAL_MS:
    TIME_UNIT_CONVERT_FACTOR.ONE_SECOND_AS_MILLISECONDS,
  I18N: {
    TOAST: 'api_rate_limited',
    BANNER: 'api_rate_limited_banner',
    BANNER_HINT: 'api_rate_limited_banner_hint',
    RELOAD_UNSAVED_WARNING: 'api_rate_limited_reload_unsaved_warning'
  }
} as const;

export const AUTH_SCREEN = {
  SIGN_IN: 'signin',
  SIGN_UP: 'signup',
  FORGOT_PASSWORD: 'forgot-password'
};

export const SURVEY_QUESTION_CATEGORY = {
  CHOICES_NUMBER: 'CHOICES_NUMBER',
  CHOICES_BOOLEAN: 'CHOICES_BOOLEAN',
  TEXT_AND_RATING: 'TEXT_AND_RATING'
} as const;

export const WINDOW_ORIGIN = {
  OPAQUE_NULL: 'null'
} as const;

export const MIN_PASSWORD_LENGTH = 12;
export const DEFAULT_SUGGESTION_DURATION_MINUTES = 10;
export const RECENTLY_ADDED_HABITS = 'Recently added habits';
export const REACT_FLAGS_SIZE = 13;
export const BREAK_FREQUENCY_MINUTES = {
  MIN: 1,
  MAX: 999
};
export const STATS_TAB_INDICES = {
  GOALS: 0,
  PROGRESS: 1,
  /** Streak — index 2 in both dashboard and embedded tab layouts. */
  STREAK: 2,
  /** Habit Stats — dashboard tabs (long_term_goals prepended, no pep talk). */
  HABIT_STATS: 4,
  /** Habit Stats — embedded tabs (includes pep talk before leaderboard). */
  HABIT_STATS_EMBEDDED: 5
};
export const LOCAL_STORAGE_TRUE = 'true';
export const LOCAL_STORAGE_FALSE = 'false';
export const MOBILE_PLATFORMS = new Set([
  PLATFORMS.ANDROID,
  PLATFORMS.IOS,
  PLATFORMS.MOB
]);
export const EMBEDDED_PAGE_LOADING_TITLE_CHANGE_LIMIT = {
  INIT: 0,
  MAXIMUM: 3
};
export const FORMAT_ABBR_MONTH_DAY_YEAR = 'MMM DD, YYYY';
export const DATE_FORMAT_YMD = 'YYYY-MM-DD';
export const TIME_ONLY_FORMAT = 'HH:mm';
export const LOCAL_DATE_TIME = {
  FORMAT: 'YYYY-MM-DD[T]HH:mm',
  END_OF_DAY_TIME: '23:59'
} as const;
export const FORMAT_ABBR_MONTH_DAY_YEAR_TIME = 'MMM D, YYYY hh:mm A';
export const FORMAT_LOCALIZED_MONTH_DAY_YEAR_TIME = 'MMM DD YYYY, hh:mm a';
export const FORMAT_ABBR_YEAR = 'YYYY';
export const FORMAT_ABBR_MONTH_YEAR = 'MMM YYYY';
export const TOTAL_COMPLETION_CATEGORIES_90D = 3;
export const SUPPORT_EMAIL = 'support@focusbear.io';
export const ONBOARD_ROUTINE_DURATION_MINUTES = 30;

export const TIMEOUT_DELAY = {
  DEFAULT: 1000,
  RANDOM_PROFILE: 1500,
  BANNER: 200,
  ERROR_PAGE: 2000,
  REFETCH_PROJECTS: 20000,
  TEXT_INPUT_DEBOUNCE_DELAY: 300,
  AXIOS: 30000,
  ONBOARDING_WELCOME: 800,
  ONBOARDING_REDIRECT: 2000,
  MOTIVATIONAL_SUMMARY: 60000,
  AUTH_DESKTOP_REDIRECT: 10000,
  AUTH_DESKTOP_RETRY_REDIRECT: 5000,
  MOTIVATIONAL_QUOTE: 2500,
  // Token/data must arrive within this window or the page signals the native shell it
  // is broken (per web_dashboard#3376). Kept below the Mac watchdog (12s) so the error
  // signal always reaches the Mac before it force-reloads.
  WEBVIEW_INITIALIZATION: 10000,
  ONBOARDING_PRIVACY_MODAL: 2500,
  HIGHLIGHT_SHUTDOWN_TIME: 2000,
  EMBEDDED_TITLE_CHANGE: 20000,
  BRAIN_DUMP_SAVE: 2000,
  ROUTINE_SUGGESTIONS_LOADING_VIDEO_DELAY: 250,
  INVITATION_ACCEPT_REDIRECT: 1500
};

export const NATIVE_TOKEN_REQUEST = {
  MIN_RETRIES: 0,
  MAX_RETRIES: 2,
  TIMEOUT_MS: TIMEOUT_DELAY.WEBVIEW_INITIALIZATION,
  DEBOUNCE_MS: 5000
} as const;

export const LIST_ITEM_POSITION = { DEFAULT: 0 };

export const INPUT_STEPS = { DEFAULT: 1 };

export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm';

export const ACCOUNTABILITY_BUDDY = {
  MAX_BUDDIES_PER_USER: 10,
  UNLOCK_REQUEST_COOLDOWN_HOURS: 1,
  INVITATION_EXPIRATION_DAYS: 1,
  UNLOCK_DURATION_MIN_MINUTES: 1,
  UNLOCK_DURATION_MAX_MINUTES: 1440,
  UNLOCK_REQUEST_REASON_MAX_LENGTH: 1000,
  UNLOCK_REQUEST_DEFAULT_REASON: 'No reason provided'
} as const;

export const TOKEN_VALIDATION_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const TIME_PRESSURE_SCORE = {
  SCORE_PAST_DUE: 10,
  SCORE_TODAY: 9,
  SCORE_FLOOR: 0.1
} as const;

export const PERCENTAGE_MULTIPLIER = 100;
export const PERCENT_FORMAT = {
  DEFAULT_FRACTION_DIGITS: 0
} as const;
export const DEFAULT_ALIGNMENT_SCORE = 100;
export const SECONDS_PER_MINUTE = 60;
export const DATA_LIMIT = 30;

export const STANDALONE_BRAIN_DUMP = {
  MIN_WORDS_FOR_CLEAR_INPUT: 5,
  MIN_CHARS_FOR_CLEAR_INPUT: 20,
  TEXT_AREA_ROWS: 6
};

export const JOIN_CODE = {
  MIN_BATCH_COUNT: 2,
  MAX_BATCH_COUNT: 100
};

export const TASK_ATTACHMENTS = {
  MAX_FILE_SIZE_BYTES: 25 * 1024 * 1024, // 25 MB
  ACCEPTED_FILE_TYPES:
    'image/*,application/pdf,text/plain,application/msword,' +
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
    'application/vnd.ms-excel,' +
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
};

export const MAX_VISIBLE_TAGS = 2;

export const SWIPE_THRESHOLDS = {
  SWIPE_DELETE: 100,
  NO_SWIPE: 0,
  SWIPE_LOCK_THRESHOLD: 10
};

export const SWIPE_DIRECTION = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical'
} as const;

export const SURVEY_FEEDBACK = {
  MAX_LENGTH: 500,
  ROWS: 4,
  DEFAULT_POSITION: 0
};

export const DEFAULT_TASKS_BY_STATUS: TodoTasksByStatus = {
  [TO_DO_STATUS.NOT_STARTED]: [],
  [TO_DO_STATUS.IN_PROGRESS]: [],
  [TO_DO_STATUS.COMPLETED]: []
};

export const TO_DO_VISIBLE_STATUSES = {
  MIN: 0,
  MAX: 3
};

export const PLAYER_TIME_LABEL_FORMATS = ['h:mm a', 'h:mm:ss a'];

export const FOCUS_SUMMARY_FORM_STATE = {
  MAX_INTENTION_FIELDS_LENGTH: 400,
  NUMBER_OF_DAYS: 7,
  TEXT_AREA_ROWS: 2,
  DEFAULT_SESSION_GAP_MINUTES: 30
};

export const FOCUS_SUMMARY_VALIDATION_KEY = {
  SELECT_BOTH_TIMES: 'weekly_focus_block_summary.select_both_times',
  FINISH_MUST_BE_AFTER_START:
    'weekly_focus_block_summary.finish_must_be_after_start',
  START_TIME_CANNOT_BE_FUTURE:
    'weekly_focus_block_summary.start_time_cannot_be_future',
  FINISH_TIME_CANNOT_BE_FUTURE:
    'weekly_focus_block_summary.finish_time_cannot_be_future'
} as const;

export const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*?>/i;

export const WEBVIEW_TODO_TAB_INDEX = {
  TASKS: 0,
  PROJECTS: 1
} as const;

/** DOM attribute used to scroll/focus a habit row from validation errors. */
export const HABIT_ROW_DATA_ATTR = 'data-habit-id';

export const StatOptions: Record<string, RoutineLevelStatType> = {
  MORNING: 'morning',
  EVENING: 'evening',
  FOCUS_MODE: 'focus_mode'
};

export const DEFAULT_ROUTINE_LEVEL = {
  AVERAGE_COMPLETION_PERCENTAGE: 0
};
